import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { union } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  CalendarEvent,
  ChatSessionType,
  EnergyFeedbackType,
  MealSignalData,
  Signal,
} from '@demind-inc/core';

import {
  buildContextMessageFromMeal,
  buildContextMessageFromSleep,
  getAISuggestedEventPermissions,
  isTemporalAISuggestedEventId,
  plainTextFromHtmlDescription,
} from '../helpers';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  acceptedEventScheduleIdsAtom,
  aiScheduledActionsAtom,
  getMealTypeFromName,
  isFutureDate,
  selectedDateAtom,
  sidebarOpenAtom,
  SleepWakeTimeISOStringSet,
  useCircadianContext,
  useMetricsContext,
  useAuthContext,
  useAssistantChat,
  useEventActions,
  useHideRoutine,
  useRecurringEventScopeModal,
  useUpdateCalendarEventEnergy,
  useTaskActions,
  generalSnackBarAtom,
} from '../../../../../data-access';
import {
  getAiScheduledActionStableId,
  transformCalendarEventFromToSessionType,
} from '../../../../../helpers';
import {
  useEditMeal,
  useEditSleep,
  useAllPlanSuggestedModal,
  EditMealParam,
} from '../../../../../hooks';
import { mergeMealDetails } from '../../../../../components';
import { useTranslation } from 'react-i18next';

interface UseAISuggestedHandlerProps {
  aiSuggestedEvent?: Partial<CalendarEvent>;
  handleAISuggestedEventModalClose: () => void;
  patchAISuggestedEvent?: (
    patch:
      | Partial<CalendarEvent>
      | ((prev: Partial<CalendarEvent> | undefined) => Partial<CalendarEvent> | undefined)
  ) => void;
}

export const useAISuggestedHandler = ({
  aiSuggestedEvent,
  handleAISuggestedEventModalClose,
  patchAISuggestedEvent,
}: UseAISuggestedHandlerProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useRecoilState(selectedDateAtom);
  const setSidebarOpen = useSetRecoilState(sidebarOpenAtom);

  const { user } = useAuthContext();
  const {
    startExplanationChat,
    sendMessage,
    setUserContext,
    removeTemporalSuggestedEvent,
    setOpenAssistantChat,
    handlePlanEntry,
  } = useAssistantChat();
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);
  const { rerunAllSuggestions } = useAllPlanSuggestedModal();
  const {
    handleEditSleepData,
    isEditingData: isEditingSleepData,
    isUpdatingSleepPrediction,
    handleEditSleepPrediction,
  } = useEditSleep({
    date: selectedDate,
    onAfterEdit: rerunAllSuggestions,
  });
  const { createCalendarEvent, eventCreatingStatus, updateCalendarEvent, eventUpdatingStatus } =
    useEventActions();
  const { updateTodoTaskStatus, isTaskStatusUpdating } = useTaskActions();
  const { getSleepOnDate, getMealOnDate } = useMetricsContext();
  const { deleteCalendarEvent } = useEventActions();
  const { hideRoutine } = useHideRoutine();
  const { requestRecurringEventScope } = useRecurringEventScopeModal();
  const { findPhaseForTaskTime } = useCircadianContext();
  const {
    isEditingData: isEditingMealData,
    handleEditMealData,
    handleEditMealPrediction,
    isUpdatingMealPrediction,
  } = useEditMeal({
    date: selectedDate,
  });
  const { updateCalendarEventEnergy, isPending: isUpdatingEnergyFeedback } =
    useUpdateCalendarEventEnergy();
  const [, setAiScheduledActions] = useRecoilState(aiScheduledActionsAtom);
  const [, setAcceptedEventScheduleIds] = useRecoilState(acceptedEventScheduleIdsAtom);

  const { name, startTime, endTime, description, sessionType } = useMemo(() => {
    if (!aiSuggestedEvent) {
      return {
        name: '',
        startTime: '',
        endTime: '',
        description: '',
        sessionType: 'sleep' as const,
      };
    }

    const sessionType = transformCalendarEventFromToSessionType(
      aiSuggestedEvent.scheduleFrom || aiSuggestedEvent.autoScheduleFrom?.from
    );
    return {
      name: aiSuggestedEvent.summary || '',
      startTime: aiSuggestedEvent.start?.date || '',
      endTime: aiSuggestedEvent.end?.date || '',
      description: aiSuggestedEvent.description || '',
      sessionType,
    };
  }, [aiSuggestedEvent]);

  const isSaving = useMemo(() => {
    return (
      isEditingSleepData ||
      isEditingMealData ||
      isUpdatingSleepPrediction ||
      isUpdatingMealPrediction ||
      eventUpdatingStatus === 'pending' ||
      eventCreatingStatus === 'pending'
    );
  }, [
    isEditingSleepData,
    isEditingMealData,
    isUpdatingSleepPrediction,
    isUpdatingMealPrediction,
    eventUpdatingStatus,
    eventCreatingStatus,
  ]);

  // Get sleep data for sleep session type
  const sleepData = useMemo(() => {
    if (sessionType !== 'sleep') return null;
    return getSleepOnDate(selectedDate);
  }, [sessionType, getSleepOnDate, selectedDate]);

  const { mealData, mealSummary } = useMemo(() => {
    if (sessionType !== 'meal' || !aiSuggestedEvent?.summary)
      return { mealData: null, mealSummary: undefined };
    const mealsRaw = getMealOnDate(selectedDate);
    const meals = mealsRaw?.details ?? [];
    const mealSummary = mealsRaw?.summary;
    const mealType = getMealTypeFromName(aiSuggestedEvent.summary);
    const targetMeals = meals.filter((m) => m.type === mealType);
    return { mealData: mergeMealDetails(targetMeals), mealSummary };
  }, [sessionType, getMealOnDate, selectedDate, aiSuggestedEvent]);

  const { isPredicted, isEditedManually } = useMemo(() => {
    if (!aiSuggestedEvent) {
      return { isPredicted: false, isEditedManually: false };
    }
    return {
      isPredicted: !!aiSuggestedEvent.autoScheduleFrom?.predicted,
      isEditedManually: !!aiSuggestedEvent.autoScheduleFrom?.editedManually,
    };
  }, [aiSuggestedEvent]);

  const isFutureEvent = useMemo(() => {
    if (!aiSuggestedEvent) {
      return false;
    }
    if (sessionType === 'sleep') {
      return isFutureDate(aiSuggestedEvent?.end?.date, 'day');
    } else if (sessionType === 'meal') {
      return isFutureDate(aiSuggestedEvent?.start?.date, 'hour');
    }
    return false;
  }, [aiSuggestedEvent, sessionType]);

  const { mealType, canUpdate, canAcceptCalendar, canReject, canDelete } = useMemo(() => {
    const mealType = getMealTypeFromName(name || '');

    const { canUpdate, canAcceptCalendar, canReject, canDelete } = getAISuggestedEventPermissions({
      eventId: aiSuggestedEvent?.eventId,
      sessionType,
      mealType: mealType || undefined,
    });

    return {
      mealType,
      canUpdate,
      canAcceptCalendar,
      canReject,
      canDelete,
    };
  }, [aiSuggestedEvent, sessionType, name]);

  const isTemporalAISuggestedEvent = useMemo(
    () => isTemporalAISuggestedEventId(aiSuggestedEvent?.eventId),
    [aiSuggestedEvent?.eventId]
  );

  const removeAISchedulerAction = useCallback(
    (eventId?: string, actionId?: string) => {
      if (!actionId || !eventId?.startsWith('ai_scheduler')) {
        return;
      }
      setAiScheduledActions((prev) =>
        prev.filter((action) => getAiScheduledActionStableId(action) !== actionId)
      );
    },
    [setAiScheduledActions]
  );

  const handleAskAI = useCallback(() => {
    if (!sessionType) {
      return;
    }
    let context = '';
    if (isPredicted) {
      context = plainTextFromHtmlDescription(description);
    } else if (sessionType === 'sleep' && sleepData) {
      context = buildContextMessageFromSleep(sleepData);
    } else if (sessionType === 'meal' && mealData) {
      context = buildContextMessageFromMeal(mealData);
    }

    // Set user context with event description based on session type
    if (context) {
      const contextKey = {
        sleep: 'sleep_detailed_explanation',
        meal: 'meals_detailed_explanation',
        task: 'task_detailed_explanation',
        general: undefined,
        scheduler: undefined,
        recommendation: undefined,
      }[sessionType];

      if (contextKey) {
        setUserContext({
          [contextKey]: context,
        });
      }
    }
    handleAISuggestedEventModalClose();
    startExplanationChat(sessionType as ChatSessionType);

    requestAnimationFrame(() => {
      setSidebarOpen(true);
      setOpenAssistantChat(true);
    });
  }, [
    sessionType,
    startExplanationChat,
    description,
    sendMessage,
    setUserContext,
    handleAISuggestedEventModalClose,
    sleepData,
    mealData,
    setSidebarOpen,
  ]);

  const handleUpdate = useCallback(
    async (start: string, end: string, title?: string) => {
      if (!sessionType) {
        return;
      }

      if (sessionType === 'sleep') {
        const newData: SleepWakeTimeISOStringSet = {
          sleepTime: start,
          wakeTime: end,
        };
        if (isFutureEvent) {
          handleEditSleepPrediction({
            newData,
            shouldUpdateEvent: true,
            baseCalendarId: aiSuggestedEvent?.calendarId,
          });
        } else {
          handleEditSleepData(newData);
        }
        requestAnimationFrame(() => {
          handleAISuggestedEventModalClose();
        });
        return;
      }
      if (sessionType === 'meal' && mealType) {
        const resolvedTitle = mealData?.title?.trim() || '';
        const newData: EditMealParam = {
          mealType,
          startTime: start,
          endTime: end,
          ...(resolvedTitle ? { mealContent: resolvedTitle, title: resolvedTitle } : {}),
          ...(mealData?.foodItems && mealData.foodItems.length > 0
            ? { foodItems: mealData.foodItems }
            : {}),
          ...(mealData?.macros ? { macros: mealData.macros } : {}),
          ...(mealData?.micros ? { micros: mealData.micros } : {}),
        };
        if (isFutureEvent) {
          handleEditMealPrediction({
            newData,
            shouldUpdateEvent: true,
            baseCalendarId: aiSuggestedEvent?.calendarId,
          });
        } else {
          handleEditMealData(newData);
        }

        requestAnimationFrame(() => {
          handleAISuggestedEventModalClose();
        });
        return;
      } else if (sessionType === 'task') {
        if (!aiSuggestedEvent?.eventId || !aiSuggestedEvent?.calendarId) {
          return;
        }
        const newDate: Pick<CalendarEvent, 'start' | 'end' | 'summary'> = {
          summary: title || name,
          start: {
            date: start,
            timeZone: dayjs.tz.guess(),
          },
          end: {
            date: end,
            timeZone: dayjs.tz.guess(),
          },
        };
        updateCalendarEvent({
          eventId: aiSuggestedEvent?.eventId,
          calendarId: aiSuggestedEvent?.calendarId,
          newEventOption: { ...aiSuggestedEvent, ...newDate },
        });

        requestAnimationFrame(() => {
          handleAISuggestedEventModalClose();
        });
        return;
      } else if (sessionType === 'routine') {
        if (!aiSuggestedEvent?.eventId || !aiSuggestedEvent?.calendarId) {
          return;
        }
        const newDate: Pick<CalendarEvent, 'start' | 'end'> = {
          start: {
            date: start,
            timeZone: dayjs.tz.guess(),
          },
          end: {
            date: end,
            timeZone: dayjs.tz.guess(),
          },
        };
        updateCalendarEvent({
          eventId: aiSuggestedEvent?.eventId,
          calendarId: aiSuggestedEvent?.calendarId,
          newEventOption: { ...aiSuggestedEvent, ...newDate },
          skipTaskUpdate: true,
        });
        requestAnimationFrame(() => {
          handleAISuggestedEventModalClose();
        });
        return;
      } else if (sessionType === 'workout') {
        if (!aiSuggestedEvent?.eventId || !aiSuggestedEvent?.calendarId) {
          return;
        }
        const newDate: Pick<CalendarEvent, 'start' | 'end' | 'summary'> = {
          summary: title || name,
          start: {
            date: start,
            timeZone: dayjs.tz.guess(),
          },
          end: {
            date: end,
            timeZone: dayjs.tz.guess(),
          },
        };
        updateCalendarEvent({
          eventId: aiSuggestedEvent?.eventId,
          calendarId: aiSuggestedEvent?.calendarId,
          newEventOption: { ...aiSuggestedEvent, ...newDate },
        });
        requestAnimationFrame(() => {
          handleAISuggestedEventModalClose();
        });
        return;
      }
    },
    [
      sessionType,
      handleEditSleepData,
      handleAISuggestedEventModalClose,
      handleEditMealData,
      mealData,
      mealType,
      updateCalendarEvent,
      user.userId,
      findPhaseForTaskTime,
    ]
  );

  const handleAccept = useCallback(
    (calendarId: string, start: string, end: string) => {
      if (!aiSuggestedEvent || !calendarId || !aiSuggestedEvent.eventId) {
        return;
      }

      const isTemporaryEvent = isTemporalAISuggestedEvent;
      const nextEventId = isTemporaryEvent ? uuidv4().replace(/-/g, '') : aiSuggestedEvent.eventId;

      const newEvent: Partial<CalendarEvent> = {
        ...aiSuggestedEvent,
        eventId: nextEventId,
        autoScheduleFrom: {
          ...aiSuggestedEvent.autoScheduleFrom!,
          predicted: false,
        },
        description: plainTextFromHtmlDescription(aiSuggestedEvent.description ?? ''),
        start: {
          date: dayjs(start).toISOString(),
          timeZone: dayjs.tz.guess(),
        },
        end: {
          date: dayjs(end).toISOString(),
          timeZone: dayjs.tz.guess(),
        },
      };

      removeTemporalSuggestedEvent(aiSuggestedEvent.eventId);
      createCalendarEvent({
        calendarId: calendarId,
        newEventOption: newEvent,
      });

      if (isTemporaryEvent) {
        const aiScheduledEventId = aiSuggestedEvent.autoScheduleFrom?.id;
        if (aiScheduledEventId) {
          setAcceptedEventScheduleIds((prev) => union(prev ?? [], [aiScheduledEventId]));
        }
      }

      handleAISuggestedEventModalClose();
    },
    [
      aiSuggestedEvent,
      isTemporalAISuggestedEvent,
      createCalendarEvent,
      removeTemporalSuggestedEvent,
      setAcceptedEventScheduleIds,
      handleAISuggestedEventModalClose,
    ]
  );

  const handleToggleTaskComplete = useCallback(
    (completed: boolean, mutateDate?: string) => {
      if (!aiSuggestedEvent?.taskFrom) {
        return;
      }

      const date =
        mutateDate ||
        (startTime ? dayjs(startTime).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));

      updateTodoTaskStatus({
        taskId: aiSuggestedEvent.taskFrom.taskId,
        boardId: aiSuggestedEvent.taskFrom.boardId,
        completed,
        mutateParams: { date },
      });
    },
    [aiSuggestedEvent, startTime, updateTodoTaskStatus]
  );

  const handleToggleRoutineComplete = useCallback(
    (completed: boolean) => {
      if (
        !aiSuggestedEvent?.eventId ||
        !aiSuggestedEvent?.calendarId ||
        !aiSuggestedEvent.routineFrom
      ) {
        return;
      }

      updateCalendarEvent({
        eventId: aiSuggestedEvent.eventId,
        calendarId: aiSuggestedEvent.calendarId,
        newEventOption: {
          routineFrom: {
            ...aiSuggestedEvent.routineFrom,
            completed,
          },
        },
      });
    },
    [aiSuggestedEvent, updateCalendarEvent]
  );

  const handleSaveMealContent = useCallback(
    async (mealInput: {
      title: string;
      foodItems: import('@demind-inc/core').MealFoodAmount[];
    }) => {
      if (!mealType || !aiSuggestedEvent?.start?.date || !aiSuggestedEvent?.end?.date) {
        return;
      }
      const prevRichContext = aiSuggestedEvent.richContext;
      const optimisticMealSignal = {
        signal_source: 'meal',
        body: mealType,
        data: {
          type: mealType,
          title: mealInput.title,
          food_items: mealInput.foodItems,
          start_time: aiSuggestedEvent.start.date,
          end_time: aiSuggestedEvent.end.date,
        } as MealSignalData,
      } as Signal;
      patchAISuggestedEvent?.({
        richContext: {
          ...prevRichContext,
          signals: [
            ...(prevRichContext?.signals || []).filter((signal) => signal.signal_source !== 'meal'),
            optimisticMealSignal,
          ],
        },
      });

      const isFutureEvent = isFutureDate(aiSuggestedEvent.start.date, 'hour');
      const newData: EditMealParam = {
        mealType,
        startTime: aiSuggestedEvent.start.date,
        endTime: aiSuggestedEvent.end.date,
        title: mealInput.title,
        foodItems: mealInput.foodItems,
      };
      try {
        if (isFutureEvent) {
          await handleEditMealPrediction({
            newData,
            shouldUpdateEvent: true,
            baseCalendarId: aiSuggestedEvent.calendarId,
          });
        } else {
          await handleEditMealData(newData);
        }
      } catch (error) {
        patchAISuggestedEvent?.({ richContext: prevRichContext });
        throw error;
      }
    },
    [
      mealType,
      aiSuggestedEvent,
      handleEditMealData,
      handleEditMealPrediction,
      patchAISuggestedEvent,
    ]
  );

  const handleReject = useCallback(() => {
    if (!aiSuggestedEvent) {
      return;
    }

    removeAISchedulerAction(aiSuggestedEvent.eventId, aiSuggestedEvent.autoScheduleFrom?.id);
    if (aiSuggestedEvent.eventId) {
      removeTemporalSuggestedEvent(aiSuggestedEvent.eventId);
    }
    handleAISuggestedEventModalClose();
  }, [aiSuggestedEvent, removeTemporalSuggestedEvent, removeAISchedulerAction]);

  const handleDelete = useCallback(async () => {
    if (!aiSuggestedEvent || !aiSuggestedEvent.calendarId || !aiSuggestedEvent.eventId) {
      return false;
    }

    try {
      if (
        sessionType === 'routine' &&
        !isPredicted &&
        aiSuggestedEvent.routineFrom?.routineActivityId &&
        user?.routineId
      ) {
        const scope = await requestRecurringEventScope({
          title: t('calendar.recurringScope.deleteTitle'),
          message: t('calendar.recurringScope.deleteMessage'),
          thisEventLabel: t('calendar.recurringScope.deleteThisEvent'),
          allEventsLabel: t('calendar.recurringScope.deleteAllEvents'),
        });

        if (scope === 'cancel') {
          return false;
        }

        if (scope === 'allEvents') {
          const deleteAllEventsPromise = hideRoutine({
            routineId: user.routineId,
            activityId: aiSuggestedEvent.routineFrom.routineActivityId,
          });
          handleAISuggestedEventModalClose();
          await deleteAllEventsPromise;
          return true;
        }
      }

      const deleteEventPromise = deleteCalendarEvent({
        event: aiSuggestedEvent,
        calendarId: aiSuggestedEvent.calendarId,
        unSyncToCalendar: false,
        ...(sessionType === 'routine' ? { recurringDeleteSpan: 'thisEvent' as const } : {}),
      });
      handleAISuggestedEventModalClose();
      await deleteEventPromise;
      return true;
    } catch {
      // `deleteCalendarEvent` already reports user-facing failures (e.g. Apple calendar unsupported on web).
      return false;
    }
  }, [
    aiSuggestedEvent,
    sessionType,
    isPredicted,
    user,
    requestRecurringEventScope,
    hideRoutine,
    deleteCalendarEvent,
    handleAISuggestedEventModalClose,
    t,
  ]);

  const handleRefresh = useCallback(async () => {
    if (!sessionType) {
      return;
    }

    let targetDate = dayjs(selectedDate);
    let dateToBeSuggested = targetDate.format('YYYY-MM-DD');

    // For sleep events, check if the end time is after midnight
    // If so, we need to set the date to the previous day
    if (sessionType === 'sleep' && aiSuggestedEvent?.end?.date) {
      const endTime = dayjs(aiSuggestedEvent.end.date);
      const endHour = endTime.hour();
      const isSelectedDateSameAsEventDate =
        selectedDate.format('YYYY-MM-DD') === endTime.format('YYYY-MM-DD');

      // If end time is before 6 pm, we want to go back to previous day to get a new suggestion
      if (endHour < 18 && isSelectedDateSameAsEventDate) {
        const previousDay = endTime.subtract(1, 'day');
        setSelectedDate(previousDay);
        targetDate = previousDay;
        dateToBeSuggested = previousDay.format('YYYY-MM-DD');
      }
    }

    const userMessageByType: Record<string, string> = {
      sleep: 'Plan my sleep today',
      meal: 'Plan my meal today',
      task: 'Plan my tasks today',
    };
    const userMessage = userMessageByType[sessionType] || 'Plan my sleep today';

    handleAISuggestedEventModalClose();
    setSidebarOpen(true);
    setOpenAssistantChat(true);
    await handlePlanEntry(sessionType as ChatSessionType, dateToBeSuggested, userMessage);
  }, [
    sessionType,
    aiSuggestedEvent,
    selectedDate,
    setSelectedDate,
    handlePlanEntry,
    handleAISuggestedEventModalClose,
    setSidebarOpen,
    setOpenAssistantChat,
  ]);

  const handleSaveEnergyFeedback = useCallback(
    async (feedback: EnergyFeedbackType) => {
      if (!aiSuggestedEvent?.eventId) {
        return;
      }

      const calendarId = aiSuggestedEvent.calendarId;
      if (!calendarId) {
        return;
      }

      await updateCalendarEventEnergy({
        calendarId,
        eventId: aiSuggestedEvent.eventId,
        eventMetrics: { energy: feedback },
        mutateParams: {
          date: dayjs(startTime).format('YYYY-MM-DD'),
        },
      });
    },
    [aiSuggestedEvent, startTime, updateCalendarEventEnergy]
  );

  return {
    isSaving,
    isEditedManually,
    canUpdate,
    canAcceptCalendar,
    canReject,
    canDelete,
    isPredicted,
    isFutureEvent,
    isTemporalAISuggestedEvent,
    mealType,
    sleepData,
    mealData,
    mealSummary,
    name,
    startTime,
    endTime,
    description,
    sessionType,
    handleAskAI,
    handleUpdate,
    handleAccept,
    handleReject,
    handleDelete,
    handleRefresh,
    handleSaveEnergyFeedback,
    handleToggleTaskComplete,
    handleToggleRoutineComplete,
    handleSaveMealContent,
    isTaskStatusUpdating,
    routineCompleted: aiSuggestedEvent?.routineFrom?.completed ?? false,
    isRoutineStatusUpdating: eventUpdatingStatus === 'pending',
  };
};
